import { ReactNode, useEffect } from 'react';
import { ErrorCard } from 'components/ErrorCard/ErrorCard';
import { LoadingSpinner } from 'components/LoadingSpinner/LoadingSpinner';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { usePathname } from 'hooks/usePathname/usePathname';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCookie } from 'utils/cookieHelper';
import { PATH_NAMES } from 'utils/routes/pathNames';

type TProps = {
  children: ReactNode;
};

export const ProductsLoader = ({ children }: TProps) => {
  const navigate = useNavigate();
  const pathname = usePathname();
  const [searchParams] = useSearchParams();
  const {
    isIntroducerError,
    isContextError,
    contextError,
    hasProductsForApplication,
    introducer,
    hasOnboardingFilesUploaded,
  } = useIntroducerContext();
  const isLoggedIn = getCookie('isLoggedIn') === 'True';
  const isForbidden = contextError?.message === 'Forbidden';
  const faultyImpersonation =
    (isForbidden || contextError?.message === 'Not Found') &&
    searchParams.has('__impersonate') &&
    searchParams.get('__impersonate') !== '0';

  const excludedPaths = [
    PATH_NAMES.calculator({ __impersonate: null }),
    PATH_NAMES.products({ __impersonate: null }),
    PATH_NAMES.notFound({ __impersonate: null }),
    PATH_NAMES.forbidden({ __impersonate: null }),
    PATH_NAMES.registrationDocuments({ __impersonate: null }),
  ];

  const isExcludedPath =
    excludedPaths.includes(pathname) ||
    pathname.startsWith(PATH_NAMES.register({ __impersonate: null }));

  useEffect(() => {
    if ((isForbidden || !isLoggedIn) && !isExcludedPath) {
      navigate(PATH_NAMES.forbidden({ __impersonate: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isForbidden, isLoggedIn, isExcludedPath]);

  useEffect(() => {
    if ((isContextError || isIntroducerError) && !isExcludedPath) {
      navigate(PATH_NAMES.notFound());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isContextError, isIntroducerError, isExcludedPath]);

  useEffect(() => {
    if (
      introducer &&
      !hasOnboardingFilesUploaded &&
      !isContextError &&
      !isIntroducerError
    ) {
      navigate(PATH_NAMES.registrationDocuments());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasOnboardingFilesUploaded,
    introducer,
    isContextError,
    isIntroducerError,
  ]);

  if (faultyImpersonation) {
    return (
      <ErrorCard code={0} message="Don't impersonate like this silly billy!" />
    );
  }

  if (isExcludedPath) {
    return <>{children}</>;
  }

  /**
   * Not using the isLoading or isFetching here because there are instances of
   * isLoading being true while data is still being fetched.
   */
  if (hasProductsForApplication) {
    return <>{children}</>;
  } else if (introducer && hasOnboardingFilesUploaded) {
    navigate(PATH_NAMES.products());
  }

  return <LoadingSpinner />;
};
